import { Organisation, OrganisationPage } from "../../../../AppDataProvider/AppDataProvider";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { applyOpacity } from "../../../../common/styles/utils";
import { corners } from "../../../../common/styles";
import { Marker } from "../Marker/Marker";
type Coordinates = {
  latitude: number;
  longitude: number;
};
export type OnSelectOrganisationOpts = {
  organisation: Organisation;
};
type MapProps = {
  highlightOrgCardOnMapPinSelect?: boolean;
  onSelectOrganisation: (options: OnSelectOrganisationOpts) => void;
  organisations: OrganisationPage;
  searchCoordinates: Coordinates;
};
export function Map(props: MapProps): ReactElement {
  const {
    organisations,
    searchCoordinates,
    onSelectOrganisation,
    highlightOrgCardOnMapPinSelect
  } = props;
  const [map, setMap] = useState<mapkit.Map>();
  const mapRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<mapkit.Coordinate | null>();
  const theme = useTheme();
  type ShowMarkerPopupOpts = {
    organisation: Organisation;
    organisationCount: number;
  };
  const [organisationMarkerPopup, setOrganisationMarkerPopup] = useState<ShowMarkerPopupOpts | null>(null);
  useEffect(() => {
    if (mapkit && mapRef.current) {
      if (!map) {
        /**
         * We only want to initialise the map once
         * otherwise we end up having duplicate maps
         * being adding into the same mapRef.
         */
        const map = new mapkit.Map(mapRef.current, {
          mapType: "standard",
          showsCompass: "hidden",
          showsMapTypeControl: false,
          showsScale: "false",
          showsUserLocation: false,
          showsUserLocationControl: false,
          showsZoomControl: true
        });
        setMap(map);
      } else {
        if (map.annotations && map.annotations.length > 0) {
          map.removeAnnotations(map.annotations);
        }
        const coords = new mapkit.Coordinate(searchCoordinates.latitude, searchCoordinates.longitude);
        if (selected) {
          map.setCenterAnimated(selected, false);
        }
        new mapkit.CoordinateSpan(0.2, 0.2);
        const searchedCoordinatesMarkerFactory = (): Element => {
          const div = document.createElement("div");
          div.className = "myLocationMarker";
          return div;
        };
        const searchedCoordinatesMarker = new mapkit.Annotation(coords, searchedCoordinatesMarkerFactory, {
          anchorOffset: new DOMPoint(0, -8)
        });
        map.addAnnotation(searchedCoordinatesMarker);
        const showMarkerPopup = (options: ShowMarkerPopupOpts) => {
          setOrganisationMarkerPopup(options);
        };
        const organisationMarkers: mapkit.MarkerAnnotation[] = organisations.edges.filter(({
          node
        }) => {
          return node.location && node.location.latitude && node.location.longitude;
        }).map(({
          node
        }, index) => {
          /**
           * We cast the location object to include coordinate values
           * as we know that organisations without a location are
           * filtered out prior to this
           */
          const location = node.location as {
            latitude: string;
            longitude: string;
          };
          const coords = new mapkit.Coordinate(parseFloat(location.latitude), parseFloat(location.longitude));
          const marker = new mapkit.MarkerAnnotation(coords, {
            color: selected?.latitude == coords.latitude && selected?.longitude == coords.longitude ? theme.colours.secondaryText : theme.colours.primaryText,
            glyphText: `${index + 1}`
          });
          map.addAnnotation(marker);
          marker.addEventListener("select", () => {
            setSelected(coords);
            if (highlightOrgCardOnMapPinSelect) {
              onSelectOrganisation({
                organisation: node
              });
            } else {
              showMarkerPopup({
                organisation: node,
                organisationCount: index + 1
              });
            }
          });
          marker.addEventListener("deselect", () => {
            setSelected(null);
          });
          return marker;
        });

        // zoom into a set of markers
        if (!selected) {
          map.setCenterAnimated(coords, false);
          map.showItems([...organisationMarkers, searchedCoordinatesMarker], {
            minimumSpan: new mapkit.CoordinateSpan(0.2, 0.2)
          });
        }
      }
    }
    // Had to disable this due to the fact that when the selected property
    // is added it causes some real weird issues with the map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, organisations, searchCoordinates, organisationMarkerPopup, theme.colours.primaryText, onSelectOrganisation, highlightOrgCardOnMapPinSelect, theme.colours.secondaryText]);
  return <>
      <style>
        {css`
          .myLocationMarker {
            height: 0.5rem;
            width: 0.5rem;
            ${corners.circle};
            background: ${theme.colours.white};
            border: 0.25rem solid ${theme.colours.primaryText};
            box-shadow:
              0rem 0rem 0.125rem 0.125rem
                ${applyOpacity(theme.colours.white, 0.5, false)},
              0rem 0rem 0rem 0.25rem
                ${applyOpacity(theme.colours.primaryText, 0.4, false)};
          }
        `}
      </style>
      <MapContainer ref={mapRef} style={{
      height: "100%",
      width: "100%"
    }} data-sentry-element="MapContainer" data-sentry-source-file="Map.tsx">
        {organisationMarkerPopup && <Marker map={map} onSelectOrganisation={onSelectOrganisation} onClosePopup={() => {
        setOrganisationMarkerPopup(null);
      }} organisation={organisationMarkerPopup.organisation} organisationCount={organisationMarkerPopup.organisationCount} />}
      </MapContainer>
    </>;
}
export default Map;
const MapContainer = styled.div``;